import React from "react";
import { useNavigate } from "react-router-dom";
import KnowledgeBaseController from "./KnowledgeBaseController";
import { SOURCE_KINDS, FILE_ACCEPT } from "./KnowledgeBaseDTO";

/**
 * 📚 KNOWLEDGE BASE (Site administration screen)
 *
 * Register the procedures, policies and records the platform is allowed to draw
 * on. Each document's text is split into passages on registration so a later
 * semantic index / assistant can retrieve and cite it.
 *
 * Cloned from precsion-med-react channels/compliance/pages/KnowledgeBasePage.js
 * and re-shaped to the SUSTINA site-admin feature conventions
 * (report_component_library).
 */
export default function KnowledgeBaseView() {
  const navigate = useNavigate();
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const c = KnowledgeBaseController(siteKey);
  const { form } = c;
  const isUrl = form.sourceKind === "url";
  const isFile = form.sourceKind === "file";
  const isText = form.sourceKind === "text";

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-2">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">Knowledge Base</h2>
                <p className="text-muted small mb-0" style={{ maxWidth: 760 }}>
                  Register a procedure, policy or record the platform is allowed
                  to use. Its text is split into passages and stored so the
                  assistant only ever answers from documents added here.
                </p>
              </div>
            </div>

            {c.error && <div className="alert alert-danger extra-small">{c.error}</div>}
            {c.notice && <div className="alert alert-info extra-small">{c.notice}</div>}

            <div className="row g-3">
              {/* ---------------- ADD DOCUMENT ---------------- */}
              <div className="col-lg-5">
                <div className="card shadow-sm border-0 bg-white p-3 h-100">
                  <h6 className="extra-small fw-bold text-uppercase text-primary mb-3">
                    Add document
                  </h6>

                  <div className="btn-group btn-group-sm w-100 mb-3" role="group" aria-label="Source">
                    {SOURCE_KINDS.map((sk) => (
                      <button
                        key={sk.code}
                        type="button"
                        className={`btn fw-bold ${form.sourceKind === sk.code ? "btn-dark" : "btn-outline-dark"}`}
                        disabled={c.submitting}
                        onClick={() => c.setField("sourceKind", sk.code)}
                      >
                        {sk.label}
                      </button>
                    ))}
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      c.submit();
                    }}
                  >
                    {isUrl && (
                      <div className="mb-3">
                        <label className="extra-small fw-bold text-muted mb-1">Document URL</label>
                        <input
                          type="url"
                          className={`form-control form-control-sm ${c.errors.sourceUrl ? "is-invalid" : ""}`}
                          value={form.sourceUrl}
                          onChange={(e) => c.setField("sourceUrl", e.target.value)}
                          placeholder="https://www.efrag.org/.../ESRS-E1.pdf"
                        />
                        <div className="invalid-feedback">{c.errors.sourceUrl}</div>
                        <div className="form-text extra-small">
                          The server fetches the page, extracts the text and indexes it.
                          HTML, PDF, Word, Markdown, plain text and XBRL / iXBRL.
                        </div>
                      </div>
                    )}

                    {isFile && (
                      <div className="mb-3">
                        <label className="extra-small fw-bold text-muted mb-1">Choose a file</label>
                        <input
                          type="file"
                          accept={FILE_ACCEPT}
                          className={`form-control form-control-sm ${c.errors.file ? "is-invalid" : ""}`}
                          disabled={c.submitting}
                          onChange={(e) => c.setFile(e.target.files && e.target.files[0])}
                        />
                        <div className="invalid-feedback">{c.errors.file}</div>
                        <div className="form-text extra-small">
                          PDF, Word (.docx), Markdown, plain text, HTML or XBRL / iXBRL.
                          The file is uploaded, its text extracted server-side, then the
                          upload is discarded — only the text is kept.
                          {c.pickedFile && (
                            <span className="d-block text-dark">
                              {c.pickedFile.name} · {(c.pickedFile.size / 1024).toFixed(0)} KB
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="extra-small fw-bold text-muted mb-1">
                        Name
                        {!isText && (
                          <span className="text-muted fw-normal">
                            {" "}(optional — taken from the {isUrl ? "page" : "file"})
                          </span>
                        )}
                      </label>
                      <input
                        className={`form-control form-control-sm ${c.errors.name ? "is-invalid" : ""}`}
                        value={form.name}
                        onChange={(e) => c.setField("name", e.target.value)}
                        placeholder={isText ? "SOP-QA-014 Deviation Management" : "Left blank — derived from the source"}
                      />
                      <div className="invalid-feedback">{c.errors.name}</div>
                    </div>

                    <div className="row g-2">
                      <div className="col-7 mb-3">
                        <label className="extra-small fw-bold text-muted mb-1">Type</label>
                        <select
                          className={`form-select form-select-sm ${c.errors.documentTypeCode ? "is-invalid" : ""}`}
                          value={form.documentTypeCode}
                          onChange={(e) => c.setField("documentTypeCode", e.target.value)}
                        >
                          {c.documentTypes.map((t) => (
                            <option key={t.code} value={t.code}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        <div className="invalid-feedback">{c.errors.documentTypeCode}</div>
                      </div>
                      <div className="col-5 mb-3">
                        <label className="extra-small fw-bold text-muted mb-1">Category</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.categoryTag}
                          onChange={(e) => c.setField("categoryTag", e.target.value)}
                          placeholder="e.g. Climate"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="extra-small fw-bold text-muted mb-1">Effective date</label>
                      <input
                        type="date"
                        className={`form-control form-control-sm ${c.errors.effectiveDate ? "is-invalid" : ""}`}
                        value={form.effectiveDate}
                        onChange={(e) => c.setField("effectiveDate", e.target.value)}
                      />
                      <div className="invalid-feedback">{c.errors.effectiveDate}</div>
                    </div>

                    {isText && (
                      <div className="mb-3">
                        <label className="extra-small fw-bold text-muted mb-1">Document text</label>
                        <textarea
                          className={`form-control form-control-sm ${c.errors.body ? "is-invalid" : ""}`}
                          rows={10}
                          style={{ fontSize: "0.85rem" }}
                          value={form.body}
                          onChange={(e) => c.setField("body", e.target.value)}
                          placeholder="Paste the full text of the procedure or record here."
                        />
                        <div className="invalid-feedback">{c.errors.body}</div>
                        {form.body.trim() && (
                          <div className="form-text extra-small">
                            ≈ {c.pendingChunkCount} passage{c.pendingChunkCount === 1 ? "" : "s"} will be indexed.
                          </div>
                        )}
                      </div>
                    )}

                    {c.generalError && (
                      <div className="alert alert-danger py-2 extra-small">{c.generalError}</div>
                    )}

                    {c.submitting && (
                      <div className="alert alert-info py-2 extra-small d-flex align-items-center gap-2">
                        <span className="spinner-border spinner-border-sm" />
                        <span>
                          {isText
                            ? "Indexing the passages…"
                            : "Extracting and indexing… a large PDF or XBRL filing can take a moment."}
                        </span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-dark btn-sm w-100 fw-bold py-2"
                      disabled={c.submitting}
                    >
                      {c.submitting
                        ? isText
                          ? "INDEXING…"
                          : "EXTRACTING & INDEXING…"
                        : isText
                          ? "INDEX DOCUMENT"
                          : "EXTRACT & INDEX"}
                    </button>
                  </form>
                </div>
              </div>

              {/* ---------------- CORPUS ---------------- */}
              <div className="col-lg-7">
                <div className="d-flex flex-wrap gap-3 mb-3 small text-muted align-items-center">
                  <span>
                    <strong className="text-dark">{c.totals.documents}</strong> documents
                  </span>
                  <span>
                    <strong className="text-dark">{c.totals.passages}</strong> passages indexed
                  </span>
                  {c.categories.length > 0 && (
                    <select
                      className="form-select form-select-sm ms-auto"
                      style={{ width: "auto" }}
                      value={c.categoryFilter}
                      onChange={(e) => c.setCategoryFilter(e.target.value)}
                    >
                      <option value="">All categories</option>
                      {c.categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {c.loading ? (
                  <div className="text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading documents…
                  </div>
                ) : c.rows.length === 0 ? (
                  <div className="alert alert-light border small">
                    No documents yet. Add one on the left.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-sm bg-white border small align-middle">
                      <thead className="table-dark">
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Effective</th>
                          <th>Status</th>
                          <th className="text-end">Passages</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {c.rows.map((d) => (
                          <tr key={d.id}>
                            <td>
                              <button
                                className="btn btn-link p-0 fw-bold text-dark text-decoration-none text-start"
                                onClick={() => c.inspect(d.id)}
                              >
                                {d.name}
                              </button>
                              {d.categoryTag && (
                                <div className="extra-small text-muted">{d.categoryTag}</div>
                              )}
                            </td>
                            <td>{d.typeLabel}</td>
                            <td>{d.effectiveDate || "n/a"}</td>
                            <td>
                              <span
                                className={`badge ${
                                  d.statusCode === "INDEXED"
                                    ? "bg-success"
                                    : d.statusCode === "FAILED"
                                      ? "bg-danger"
                                      : "bg-secondary"
                                }`}
                                style={{ fontSize: "0.6rem" }}
                              >
                                {d.statusLabel || d.statusCode}
                              </span>
                            </td>
                            <td className="text-end">{d.chunkCount}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                style={{ fontSize: "0.65rem" }}
                                onClick={() => c.removeDocument(d.id)}
                              >
                                remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ---------------- INSPECTOR ---------------- */}
                {c.openLoading && (
                  <div className="text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Opening…
                  </div>
                )}
                {c.openDoc && (
                  <div className="card border-0 shadow-sm bg-light mt-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                          {c.openDoc.name}
                        </h6>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => c.inspect(c.openDoc.knowledgeDocumentId)}
                        >
                          Close
                        </button>
                      </div>
                      <div className="extra-small text-muted mb-3">
                        {c.openDoc.documentTypeLabel || c.openDoc.documentTypeCode}
                        {c.openDoc.effectiveDate ? ` · effective ${c.openDoc.effectiveDate}` : ""}
                        {" · "}
                        {c.openDoc.chunks.length} passage{c.openDoc.chunks.length === 1 ? "" : "s"}
                        {c.openDoc.sourceUrl ? (
                          <>
                            {" · "}
                            <a href={c.openDoc.sourceUrl} target="_blank" rel="noreferrer">
                              source
                            </a>
                          </>
                        ) : null}
                      </div>
                      <ol className="mb-0 ps-3">
                        {c.openDoc.chunks.map((ch) => (
                          <li key={ch.chunkId} className="extra-small text-dark mb-2">
                            {ch.section && (
                              <span className="fw-bold d-block text-muted">{ch.section}</span>
                            )}
                            {ch.bodyText.length > 500
                              ? `${ch.bodyText.slice(0, 500)}…`
                              : ch.bodyText}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top">
              <button
                className="btn btn-link text-decoration-none p-0 text-secondary small"
                onClick={() => navigate("/registered/site-admin")}
              >
                <i className="bi bi-arrow-left me-2"></i> Return to Site Administration
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
